import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import ToastContainer from '@/components/ToastContainer';
import { chatMonitoringService } from '@/services/chatMonitoringService';

interface RouteMeta {
  title: string;
  crumb: string[];
}

const ROUTE_META: Record<string, RouteMeta> = {
  '/':                        { title: 'Visão geral',           crumb: ['IaSolicitar', 'Visão geral'] },
  '/agenda':                  { title: 'Agenda',                crumb: ['IaSolicitar', 'Geral', 'Agenda'] },
  '/tenders':                 { title: 'Editais',               crumb: ['IaSolicitar', 'Licitações', 'Editais'] },
  '/favorites':               { title: 'Licitações em Participação', crumb: ['IaSolicitar', 'Licitações', 'Em Participação'] },
  '/favorites-archived':      { title: 'Participações arquivadas', crumb: ['IaSolicitar', 'Licitações', 'Participações arquivadas'] },
  '/ongoing':                 { title: 'Em andamento',          crumb: ['IaSolicitar', 'Licitações', 'Em andamento'] },
  '/participation':           { title: 'Participação',          crumb: ['IaSolicitar', 'Licitações', 'Em andamento', 'Participação'] },
  '/consults':                { title: 'Busca de oportunidade', crumb: ['IaSolicitar', 'Licitações', 'Busca de oportunidade'] },
  '/tender-calendar':         { title: 'Calendário de oportunidades', crumb: ['IaSolicitar', 'Licitações', 'Calendário de oportunidades'] },
  '/analyst':                 { title: 'Analista IA',           crumb: ['IaSolicitar', 'Licitações', 'Analista IA'] },
  '/competitors':             { title: 'Concorrentes',          crumb: ['IaSolicitar', 'Concorrentes'] },
  '/ganhas':                  { title: 'Processos homologados', crumb: ['IaSolicitar', 'Licitações', 'Homologadas'] },
  '/resultados':              { title: 'Resultados',            crumb: ['IaSolicitar', 'Licitações', 'Resultados'] },
  '/concorrentes-resultados': { title: 'Resultados concorrentes', crumb: ['IaSolicitar', 'Concorrentes', 'Resultados'] },
  '/products':                { title: 'Produtos',              crumb: ['IaSolicitar', 'Operação', 'Produtos'] },
  '/orders':                  { title: 'Empenhos & O.S.',       crumb: ['IaSolicitar', 'Operação', 'Empenhos'] },
  '/orders/board':            { title: 'Kanban de empenhos',    crumb: ['IaSolicitar', 'Operação', 'Empenhos', 'Kanban'] },
  '/communications':          { title: 'Ofícios & prazos',      crumb: ['IaSolicitar', 'Operação', 'Ofícios'] },
  '/legal':                   { title: 'Jurídico',              crumb: ['IaSolicitar', 'Jurídico'] },
  '/chat-monitoring':         { title: 'Monitor de licitação',  crumb: ['IaSolicitar', 'Licitações', 'Em andamento', 'Monitor de licitação'] },
  '/contracts':               { title: 'Contratos',             crumb: ['IaSolicitar', 'Operação', 'Contratos'] },
  '/atestados':               { title: 'Atestados de Capacidade Técnica', crumb: ['IaSolicitar', 'Documentação', 'Atestados'] },
  '/documents':               { title: 'Documentos & certidões',crumb: ['IaSolicitar', 'Documentação', 'Documentos'] },
  '/portals':                 { title: 'Acesso a portais',      crumb: ['IaSolicitar', 'Documentação', 'Portais'] },
  '/ai-analysis':             { title: 'Análise IA',            crumb: ['IaSolicitar', 'Gestão', 'Análise IA'] },
  '/templates':               { title: 'Modelos & layouts',     crumb: ['IaSolicitar', 'Gestão', 'Modelos'] },
  '/settings':                { title: 'Configurações',         crumb: ['IaSolicitar', 'Configurações'] },
};

interface AppShellProps {
  children: ReactNode;
  user: { name?: string; role?: string; menuKeys?: string[] } | null;
  onLogout: () => void;
  onOpenAi?: () => void;
}

export function AppShell({ children, user, onLogout, onOpenAi }: AppShellProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingChatAlerts, setPendingChatAlerts] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const meta = ROUTE_META[location.pathname] || {
    title: 'IaSolicitar',
    crumb: ['IaSolicitar'],
  };

  useEffect(() => {
    let active = true;
    const refreshAlerts = async () => {
      try {
        const counts = await chatMonitoringService.getEventCounts();
        if (active) setPendingChatAlerts(counts.pending || 0);
      } catch {
        // A navegação não deve falhar se o serviço de alertas estiver temporariamente indisponível.
      }
    };
    void refreshAlerts();
    const timer = window.setInterval(refreshAlerts, 30_000);
    window.addEventListener('chat-monitoring:updated', refreshAlerts);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('chat-monitoring:updated', refreshAlerts);
    };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-bg dark:bg-bg-dark text-ink-title dark:text-ink-title-dark">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        user={user}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar
          title={meta.title}
          breadcrumb={meta.crumb}
          user={user}
          onOpenMobileMenu={() => setMobileOpen(true)}
          onOpenAi={onOpenAi}
          onLogout={onLogout}
          notifications={pendingChatAlerts}
          onOpenNotifications={() => navigate('/chat-monitoring')}
        />
        <main className="flex-1 overflow-auto">{children}</main>
        <ToastContainer />
      </div>
    </div>
  );
}
